import { queryOptions } from "@tanstack/react-query";
import { z } from "zod/v4";
import { env } from "@/env";
import { MerchantSummarySchema } from "@/types/merchant-summary";
import { parseVolumeJwt, redirectToSignIn } from "./auth";
import { MerchantAccessError, shouldRetryQuery } from "./query-retry";
import { MissingAdminRouteTokenError } from "./admin-route-context-error";

export function getAdminMerchantsQueryOptions({
  accessToken,
}: {
  accessToken: string;
}) {
  if (typeof accessToken !== "string" || accessToken.length === 0) {
    throw new MissingAdminRouteTokenError();
  }
  const session = parseVolumeJwt(accessToken);
  return queryOptions({
    queryKey: [
      "admin",
      "merchants",
      "summary",
      session.userAccountId,
      session.jti,
    ],
    // The list rarely changes: keep it for the whole tab session so the
    // switcher never shows a loading state after the first fetch, and refresh
    // it quietly in the background while the tab is visible.
    gcTime: Infinity,
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: shouldRetryQuery,
    queryFn: async ({ signal }) => {
      const response = await fetch(
        `${env.VITE_VOLUME_URL}/api/merchants/summary`,
        {
          signal,
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      if (response.status === 401) throw redirectToSignIn(false);
      if (response.status === 403) throw new MerchantAccessError();
      if (!response.ok) throw new Error("Failed to fetch merchants");
      return MerchantSummarySchema.array().parse(await response.json());
    },
  });
}

export const VA_PROVIDER_TYPES = [
  "MODULR",
  "OPTIMUS",
  "ISX",
  "DEVENGO",
  "CLEARBANK",
] as const;
export type VaProviderType = (typeof VA_PROVIDER_TYPES)[number];

// Only the fields needed to derive each merchant's enabled VA providers.
const MerchantVaProvidersSchema = z.object({
  merchantId: z.string(),
  applications: z.array(
    z.object({
      payeeDetailsPerCurrency: z
        .array(
          z.object({
            vaProviderType: z.enum(VA_PROVIDER_TYPES).nullish().catch(null),
          }),
        )
        .nullish(),
    }),
  ),
});

// The summary endpoint omits pay-in accounts, so provider filtering loads the
// full merchant list. Only enable this when a provider filter is in use.
export function getAdminMerchantVaProvidersQueryOptions({
  accessToken,
}: {
  accessToken: string;
}) {
  if (typeof accessToken !== "string" || accessToken.length === 0) {
    throw new MissingAdminRouteTokenError();
  }
  const session = parseVolumeJwt(accessToken);
  return queryOptions({
    queryKey: [
      "admin",
      "merchants",
      "va-providers",
      session.userAccountId,
      session.jti,
    ],
    // Heavy endpoint that rarely changes: keep it for the tab session so the
    // provider badges don't flash back to a loading state between visits.
    gcTime: Infinity,
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
    retry: shouldRetryQuery,
    queryFn: async ({ signal }) => {
      const response = await fetch(`${env.VITE_VOLUME_URL}/api/merchants`, {
        signal,
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.status === 401) throw redirectToSignIn(false);
      if (response.status === 403) throw new MerchantAccessError();
      if (!response.ok) throw new Error("Failed to fetch merchant providers");
      const merchants = MerchantVaProvidersSchema.array().parse(
        await response.json(),
      );
      return new Map(
        merchants.map((merchant) => {
          const types = new Set<VaProviderType>();
          for (const app of merchant.applications)
            for (const payee of app.payeeDetailsPerCurrency ?? [])
              if (payee.vaProviderType) types.add(payee.vaProviderType);
          return [merchant.merchantId, [...types]] as const;
        }),
      );
    },
  });
}
